// src/components/common/Sidebar.tsx
'use client'

import React from 'react';
import {
    Box,
    VStack,
    Text,
    Link,
    IconButton,
    Image,
    useColorModeValue
} from '@chakra-ui/react';
import { NavLink as RouterLink } from 'react-router-dom';
import { FiBriefcase, FiFileText, FiUser, FiX } from 'react-icons/fi';
import logo from "../../assets/afdtech-part-of-accenture-rectangle.png";
import { useAuth } from '../../features/auth/hooks/useAuth';
import { Role } from '../../features/auth/types/auth.types';

interface SidebarProps {
    onClose?: () => void;
}

export default function Sidebar({ onClose }: SidebarProps) {
    const linkHoverBg = useColorModeValue('white', 'gray');
    const linkColor   = useColorModeValue('white', 'gray.200');

    // Récupère les rôles de l'utilisateur
    const { userInfo } = useAuth();
    const roles = userInfo.roles;

    const isConsultant = roles.includes(Role.CONSULTANT);
    const isBD         = roles.includes(Role.BD);
    const isAdmin      = roles.includes(Role.ADMIN);

    return (
        <Box
            position="fixed"
            left="0"
            top="0"
            h="100vh"
            w="240px"
            bg={useColorModeValue(
                'radial-gradient(circle at bottom left, #420178 0%, #08000f 100%)',
                'radial-gradient(circle at top right, #05020d 0%, #fc8efe 100%)'
            )}
            p={4}
            zIndex={10}
            boxShadow="lg"
        >
            <VStack align="stretch" spacing={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box as={RouterLink} to="/" color="white">
                        <Image src={logo} alt="Connext logo" h={12} />
                    </Box>
                    {onClose && (
                        <IconButton
                            aria-label="Close Sidebar"
                            icon={<FiX />}
                            variant="ghost"
                            onClick={onClose}
                            display={{ base: 'flex', md: 'none' }}
                            color={linkColor}
                            _hover={{
                                textDecoration: 'none',
                                bg: linkHoverBg,
                                transform: 'scale(1.05)',
                                color: 'black'
                            }}
                        />
                    )}
                </Box>

                {/* Profil : tous les utilisateurs connectés */}


                {/* Missions : BD, Consultant, Admin */}
                {(isConsultant || isAdmin || isBD) && (
                    <Link
                        as={RouterLink}
                        to="/missions"
                        p={2}
                        borderRadius="md"
                        _hover={{
                            textDecoration: 'none',
                            bg: linkHoverBg,
                            transform: 'scale(1.05)',
                            color: 'black'
                        }}
                        transition="all 0.2s"
                        color={linkColor}
                        display="flex"
                        alignItems="center"
                    >
                        <FiBriefcase style={{ marginRight: '8px' }} />
                        Missions
                    </Link>
                )}

                {/* Mes Candidatures : Consultant, Admin */}
                {(isConsultant || isAdmin) && (
                    <Link
                        as={RouterLink}
                        to="/mescandidatures"
                        p={2}
                        borderRadius="md"
                        _hover={{
                            textDecoration: 'none',
                            bg: linkHoverBg,
                            transform: 'scale(1.05)',
                            color: 'black'
                        }}
                        transition="all 0.2s"
                        color={linkColor}
                        display="flex"
                        alignItems="center"
                    >
                        <FiFileText style={{ marginRight: '8px' }} />
                        Mes Candidatures
                    </Link>
                )}

                {/* Candidatures aux missions (historique BD) : BD, Admin */}
                {(isBD || isAdmin) && (
                    <Link
                        as={RouterLink}
                        to="/historique"
                        p={2}
                        borderRadius="md"
                        _hover={{
                            textDecoration: 'none',
                            bg: linkHoverBg,
                            transform: 'scale(1.05)',
                            color: 'black'
                        }}
                        transition="all 0.2s"
                        color={linkColor}
                        display="flex"
                        alignItems="center"
                    >
                        <FiFileText style={{ marginRight: '8px' }} />
                        Candidatures aux missions
                    </Link>
                )}
                {(isBD || isAdmin) && (
                    <Link
                        as={RouterLink}
                        to="/creer-mission"
                        p={2}
                        borderRadius="md"
                        _hover={{
                            textDecoration: 'none',
                            bg: linkHoverBg,
                            transform: 'scale(1.05)',
                            color: 'black'
                        }}
                        transition="all 0.2s"
                        color={linkColor}
                        display="flex"
                        alignItems="center"
                    >
                        <FiFileText style={{ marginRight: '8px' }} />
                        Cree une mission
                    </Link>
                )}
                {(isAdmin) && (
                    <Link
                        as={RouterLink}
                        to="/import-excel"
                        p={2}
                        borderRadius="md"
                        _hover={{
                            textDecoration: 'none',
                            bg: linkHoverBg,
                            transform: 'scale(1.05)',
                            color: 'black'
                        }}
                        transition="all 0.2s"
                        color={linkColor}
                        display="flex"
                        alignItems="center"
                    >
                        <FiFileText style={{ marginRight: '8px' }} />
                        Importer des missions
                    </Link>
                )}
            </VStack>
        </Box>
    );
}
